import {Injectable, OnDestroy} from '@angular/core';
import * as mapboxgl from 'mapbox-gl';
import {DatacenterV2} from '../model/datacenter-v2';
import {ColorUtils} from '../util/color-utils';
import {
  AZ_CIRCLE_BORDER_LAYER,
  AZ_CIRCLE_FILL_LAYER, AZ_CIRCLE_RADIUS_KM,
  AZ_LABEL_LAYER,
  AZ_SOURCE_ID,
  DEFAULT_AZ_CIRCLE_COLOR, MARKERS_SOURCE_ID
} from '../consts/map.constants';
import {DataCentersService} from "./data-centers.service";
import {Store} from '@ngrx/store';
import * as availabilityZoneActions from '../ngrx/actions/availability-zone.actions';
import * as turf from '@turf/turf';

export interface AvailabilityZone {
  id: string;
  availabilityZoneId: string;
  provider: string;
  providerId?: string;
  building: string;
  lat: number;
  lon: number;
  mwLive: number;
  mwUC: number;
  mwPlanned: number;
  color?: string;
  isVisible?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AvailabilityZoneHandler {
  private map: mapboxgl.Map | null = null;

  constructor(
    private datacentersService: DataCentersService,
    private store: Store
  ) {}

  /**
   * Sets the map instance
   */
  setMap(map: mapboxgl.Map): void {
    this.map = map;
  }

  /**
   * Sets the map instance to use for data extraction
   */
  public addAZData(map: mapboxgl.Map): void {
    this.setMap(map);
    try {
      // Add the GeoJSON source
      map.addSource(AZ_SOURCE_ID, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });

      map.addLayer({
        id: AZ_CIRCLE_FILL_LAYER,
        type: 'fill',
        source: AZ_SOURCE_ID,
        filter: ['==', ['get', 'type'], 'circle'],
        paint: {
          'fill-color': ['get', 'color'],
          'fill-opacity': 0.02
        }
      });

      map.addLayer({
        id: AZ_CIRCLE_BORDER_LAYER,
        type: 'line',
        source: AZ_SOURCE_ID,
        filter: ['==', ['get', 'type'], 'circle'],
        layout: {
          'line-cap': 'round',
          'line-join': 'round'
        },
        paint: {
          'line-color': ['get', 'color'],
          'line-width': 2,
          'line-dasharray': [0.2, 4]
        }
      });

      map.addLayer({
        id: AZ_LABEL_LAYER,
        type: 'symbol',
        source: AZ_SOURCE_ID,
        filter: ['==', ['get', 'type'], 'label'],
        layout: {
          'text-field': ['get', 'azId'],
          'text-size': 14,
          'text-anchor': 'center',
          'symbol-placement': 'point',
          'text-offset': [0, 2]
        },
        paint: {
          'text-color': ['get', 'color'],
          'text-halo-color': '#FFFFFF',
          'text-halo-width': 1.5
        }
      });

    } catch (error) {
      console.error('Error initializing AZ optimized layers:', error);
    }
  }

  /**
   * Gets source and feature collection with validation
   */
  private getSourceData(sourceId: string): [any, any] | null {
    if (!this.map) {
      console.debug('Map is not initialized.');
      return null;
    }

    try {
      // Check if source exists before trying to get data
      const mapSource = this.map.getSource(sourceId);
      if (!mapSource) {
        console.debug(`Source ${sourceId} not found in map.`);
        return null;
      }

      // Additional check for source data availability
      const sourceData = (mapSource as any)._data;
      if (!sourceData) {
        console.debug(`Source ${sourceId} exists but has no data yet.`);
        return null;
      }

      const [source, featureCollection] = this.datacentersService.getFeatureCollectionFromSource(this.map, sourceId);
      if (!source || !featureCollection) {
        console.debug(`Invalid source data for ${sourceId}.`);
        return null;
      }
      return [source, featureCollection];
    } catch (error) {
      console.debug(`Error getting source data for ${sourceId}:`, error);
      return null;
    }
  }

  /**
   * Finds features by ID in a feature collection
   */
  private findFeaturesByAzId(featureCollection: any, azId: string): any[] {
    return featureCollection.features.filter((feature: any) => 
      feature.properties && feature.properties['id'] === azId
    );
  }

  /**
   * Finds datacenter feature by ID in markers source
   */
  private findDatacenterFeature(azId: string): any | null {
    const sourceData = this.getSourceData(MARKERS_SOURCE_ID);
    if (!sourceData) return null;

    const [, markersFC] = sourceData;
    return markersFC.features.find((f: any) => 
      f.properties && (f.properties['id'] === azId || f.properties['auxId'] === azId)
    ) || null;
  }

  /**
   * Gets all availability zones from geojson source
   */
  public getAvailabilityZones(): AvailabilityZone[] {
    // Early return if map is not initialized
    if (!this.map) {
      console.debug('Map not initialized yet, returning empty availability zones array');
      return [];
    }

    const sourceData = this.getSourceData(MARKERS_SOURCE_ID);
    if (!sourceData) {
      console.debug('Cannot get availability zones: source data not available');
      return [];
    }

    const [, featureCollection] = sourceData;
    if (!featureCollection || !featureCollection.features || !Array.isArray(featureCollection.features)) {
      console.debug('Cannot get availability zones: invalid feature collection');
      return [];
    }

    try {
      const azDatacenters: AvailabilityZone[] = featureCollection.features
        .filter((feature: any) => feature.properties?.availabilityZoneId)
        .map((feature: any) => this.convertFeatureToAvailabilityZone(feature));
      return azDatacenters;
    } catch (error) {
      console.error('Error processing availability zones:', error);
      return [];
    }
  }


  /**
   * Gets datacenters filtered by azId
   */
  public getDatacentersByAzId(azId: string): AvailabilityZone[] {
    return this.getAvailabilityZones().filter(az => az.availabilityZoneId === azId);
  }

  /**
   * Gets availability zone IDs for a specific provider (sorted)
   */
  public getAvailabilityZoneIdsByProvider(providerName: string): { label: string; value: string }[] {
    if (!providerName || providerName.trim() === '') {
      return [];
    }

    const sourceData = this.getSourceData(MARKERS_SOURCE_ID);
    if (!sourceData) {
      return [];
    }

    const [, featureCollection] = sourceData;
    const existingAZIds = [...new Set(
      featureCollection.features
        .filter((feature: any) => 
          feature.properties?.availabilityZoneId && 
          feature.properties?.provider === providerName
        )
        .map((feature: any) => feature.properties.availabilityZoneId)
    )] as string[];

    return existingAZIds
      .sort((a, b) => a.localeCompare(b))
      .map(id => ({ label: id, value: id }));
  }

  /**
   * Convert geojson feature to AvailabilityZone
   */
  private convertFeatureToAvailabilityZone(feature: any): AvailabilityZone {
    const props = feature.properties;
    const coords = feature.geometry.coordinates;
    
    return {
      id: props.auxId || props.id,
      availabilityZoneId: props.availabilityZoneId,
      provider: props.provider || '',
      providerId: props.providerId || '',
      building: props.building || '',
      lat: coords[1] || 0,
      lon: coords[0] || 0,
      mwLive: props.mwLive || 0,
      mwUC: props.mwUC || 0,
      mwPlanned: props.mwPlanned || 0,
      color: props.providerColor || DEFAULT_AZ_CIRCLE_COLOR,
      isVisible: this.isAZVisibleInSource(props.auxId || props.id)
    };
  }

  /**
   * Applies provider colors to availability zones
   */
  public applyColorsToDatacenters(azDatacenters: AvailabilityZone[], providers: any[]): AvailabilityZone[] {
    return azDatacenters.map(az => {
      const color = az.color || ColorUtils.getProviderColor(az.provider, providers) || DEFAULT_AZ_CIRCLE_COLOR;
      return {
        ...az,
        color: color
      };
    });
  }

  /**
   * Updates the AZ position in the map when a datacenter is moved
   */
  public updateAZPositionInMap(datacenter: DatacenterV2): void {
    const sourceData = this.getSourceData(AZ_SOURCE_ID);
    if (!sourceData) return;

    const [azSource, azFeatureCollection] = sourceData;
    const azId = datacenter.auxId || datacenter.id;
    let wasUpdated = false;

    // Update circle feature
    const circleFeature = azFeatureCollection.features.find(
      f => f.properties && f.properties['id'] === azId && f.properties['type'] === 'circle'
    );
    
    if (circleFeature) {
      const point = turf.point([datacenter.lon, datacenter.lat]);
      const circleGeometry = turf.circle(point, AZ_CIRCLE_RADIUS_KM, { units: 'kilometers' });
      circleFeature.geometry = circleGeometry.geometry;
      wasUpdated = true;
    }

    // Update label feature
    const labelFeature = azFeatureCollection.features.find(
      f => f.properties && f.properties['id'] === azId && f.properties['type'] === 'label'
    );
    
    if (labelFeature && labelFeature.geometry && labelFeature.geometry.type === 'Point') {
      (labelFeature.geometry as any).coordinates = [datacenter.lon, datacenter.lat];
      wasUpdated = true;
    }

    if (wasUpdated) {
      azSource.setData(azFeatureCollection);
    }
  }

  /**
   * Toggles AZ visibility in the map by adding/removing features from source
   */
  public toggleAZVisibilityInMap(azId: string, visible: boolean): void {
    // Dispatch action to notify about visibility change
    this.store.dispatch(availabilityZoneActions.toggleAZVisibility({ azId, isVisible: visible }));

    const sourceData = this.getSourceData(AZ_SOURCE_ID);
    if (!sourceData) return;

    const [azSource, azFeatureCollection] = sourceData;
    const existingFeatures = this.findFeaturesByAzId(azFeatureCollection, azId);

    if (visible) {
      if (existingFeatures.length === 0) {
        this.addAZFeaturesToSource(azId);
      }
    } else {
      if (existingFeatures.length > 0) {
        azFeatureCollection.features = azFeatureCollection.features.filter(feature => 
          !feature.properties || feature.properties['id'] !== azId
        );
        azSource.setData(azFeatureCollection);
      }
    }
  }

  /**
   * Creates AZ feature properties
   */
  private createAZFeatureProperties(datacenterFeature: any, azId: string, type: 'circle' | 'label'): any {
    return {
      type,
      provider: datacenterFeature.properties['provider'],
      azId: datacenterFeature.properties['availabilityZoneId'],
      building: datacenterFeature.properties['building'],
      color: datacenterFeature.properties['providerColor'] || DEFAULT_AZ_CIRCLE_COLOR,
      id: azId
    };
  }

  /**
   * Adds AZ features to the GeoJSON source
   */
  private addAZFeaturesToSource(azId: string): void {
    const azSourceData = this.getSourceData(AZ_SOURCE_ID);
    if (!azSourceData) return;

    const [azSource, azFC] = azSourceData;
    const datacenterFeature = this.findDatacenterFeature(azId);

    if (!datacenterFeature || !datacenterFeature.properties['availabilityZoneId']) {
      return;
    }

    const coords = datacenterFeature.geometry.coordinates;
    const point = turf.point([coords[0], coords[1]]);
    const circleGeometry = turf.circle(point, AZ_CIRCLE_RADIUS_KM, { units: 'kilometers' });

    const circleFeature = {
      type: 'Feature',
      geometry: circleGeometry.geometry,
      properties: this.createAZFeatureProperties(datacenterFeature, azId, 'circle')
    };

    const labelFeature = {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [coords[0], coords[1]]
      },
      properties: this.createAZFeatureProperties(datacenterFeature, azId, 'label')
    };

    azFC.features.push(circleFeature as any);
    azFC.features.push(labelFeature as any);
    azSource.setData(azFC);
  }

  /**
   * Removes all AZ features (circle and label) from the map source
   */
  public removeAZFromMap(azId: string): void {
    const sourceData = this.getSourceData(AZ_SOURCE_ID);
    if (!sourceData) {
      console.warn('AZ source not found. Cannot remove AZ features.');
      return;
    }

    const [azSource, azFeatureCollection] = sourceData;
    
    // Filter out all features that belong to this AZ ID
    const initialFeatureCount = azFeatureCollection.features.length;
    azFeatureCollection.features = azFeatureCollection.features.filter(feature => 
      !feature.properties || feature.properties['id'] !== azId
    );

    const removedFeatures = initialFeatureCount - azFeatureCollection.features.length;
    if (removedFeatures > 0) {
      azSource.setData(azFeatureCollection);
      console.log(`Removed ${removedFeatures} AZ features for azId: ${azId}`);
    }
  }

  /**
   * Checks if an AZ is currently visible based on whether its features exist in the source
   */
  isAZVisibleInSource(azId: string): boolean {
    const sourceData = this.getSourceData(AZ_SOURCE_ID);
    if (!sourceData) return false;

    const [, azFeatureCollection] = sourceData;
    const existingFeatures = this.findFeaturesByAzId(azFeatureCollection, azId);
    return existingFeatures.length > 0;
  }

  /**
   * Updates AZ colors when provider color changes
   */
  public onProviderColorChanged(providerId: string, newColor: string): void {
    const azSourceData = this.getSourceData(AZ_SOURCE_ID);
    const markersSourceData = this.getSourceData(MARKERS_SOURCE_ID);
    
    if (!azSourceData || !markersSourceData) {
      console.warn('Required sources not found. Cannot update AZ colors.');
      return;
    }

    const [azSource, azFeatureCollection] = azSourceData;
    const [, markersFC] = markersSourceData;

    // Find and update all features that belong to this provider
    let updatedFeatures = 0;
    azFeatureCollection.features.forEach(feature => {
      if (feature.properties && feature.properties['provider']) {
        const datacenterFeature = markersFC.features.find(f => 
          f.properties && 
          (f.properties['id'] === feature.properties['id'] || f.properties['auxId'] === feature.properties['id']) &&
          (f.properties['providerId'] === providerId)
        );
        
        if (datacenterFeature) {
          feature.properties['color'] = newColor;
          updatedFeatures++;
        }
      }
    });

    if (updatedFeatures > 0) {
      azSource.setData(azFeatureCollection);
      console.log(`Updated ${updatedFeatures} AZ features with new color for provider: ${providerId}`);
    }
  }

  /**
   * Updates AZ ID references after a datacenter changes from auxId to definitive id
   */
  public updateAZIdAfterSave(oldAuxId: string, newId: string): void {
    const sourceData = this.getSourceData(AZ_SOURCE_ID);
    if (!sourceData) return;

    const [azSource, azFeatureCollection] = sourceData;
    let wasUpdated = false;

    // Update all AZ features that have the old auxId
    azFeatureCollection.features.forEach(feature => {
      if (feature.properties && feature.properties['id'] === oldAuxId) {
        feature.properties['id'] = newId;
        wasUpdated = true;
      }
    });

    if (wasUpdated) {
      azSource.setData(azFeatureCollection);
    }
  }
}